import * as yup from 'yup';
import { Customer, RemindingTone, ScenarioCreateInput, ScenarioPlanName, User, Vendor } from '../api/gql';
import { langKeys } from '../localization/formErrors';
import {
  InvoiceFormType,
  InvoiceManagementInvoiceFormType,
  ManualFilledInvoiceFormType,
} from '../modules/forms/invoice/types';
import { validators } from './validators';

export const vatIdSchema = yup.string().required();
export const vatIdOptionalSchema = yup.string().notRequired();

export const phoneNumberSchema = yup
  .string()
  .when('email', {
    is: (val: string | null | undefined) => !!val,
    then: (s) => s.nullable().notRequired(),
    otherwise: (s) => s.required(),
  })
  .matches(validators.phone, {
    message: langKeys.invalidPhone,
    excludeEmptyString: true,
  });

export const emailSchema = yup
  .string()
  .when('phoneNumber', {
    is: (val: string | null | undefined) => !!val,
    then: (s) => s.nullable().notRequired(),
    otherwise: (s) => s.required(),
  })
  .matches(validators.email, {
    message: langKeys.invalid,
    excludeEmptyString: true,
  });

export const customerValidationSchema: yup.ObjectSchema<Omit<Customer, 'id' | '__typename'>> = yup.object().shape(
  {
    name: yup.string().required(),
    address: yup.string().nullable(),
    email: emailSchema,
    phoneNumber: phoneNumberSchema,
    vatId: vatIdSchema,
    taxId: yup.string().nullable(),
    remindingTone: yup.mixed<RemindingTone>().oneOf(Object.values(RemindingTone)).required(),
  },
  [['email', 'phoneNumber']],
);
export const vendorValidationSchema: yup.ObjectSchema<Omit<Vendor, 'id' | '__typename'>> = yup.object({
  name: yup.string().required(),
  address: yup.string().nullable(),
  vatId: vatIdOptionalSchema,
  taxId: yup.string().nullable(),
});

export const customerContactValidationSchema: yup.ObjectSchema<Pick<Customer, 'email' | 'phoneNumber'>> = yup
  .object()
  .shape(
    {
      email: emailSchema,
      phoneNumber: phoneNumberSchema,
    },
    [['email', 'phoneNumber']],
  );

export const userValidationSchema: yup.ObjectSchema<
  Omit<User, '__typename' | 'id' | 'email' | 'role' | 'isEnterprise' | 'defaultRemindingTone'>
> = yup.object({
  firstName: yup.string().required(),
  lastName: yup.string().required(),
  phoneNumber: phoneNumberSchema,
  vatId: vatIdOptionalSchema,
  businessName: yup.string().required(),
  streetAddress: yup.string().required(),
  city: yup.string().required(),
  zipCode: yup.string().required().matches(validators.postalCode, langKeys.invalid).trim(),
  country: yup.string().required(),
  taxId: yup.string().nullable(),
  bankAccount: yup
    .string()
    .nullable()
    .test({
      name: 'isBankAccount',
      message: langKeys.invalidBankAccount,
      test: (value) => (!value ? true : /^(\d{1,6}-)?\d{1,10}$/.test(value)),
    })
    .test({
      name: 'bankAccountWithBankNumber',
      message: langKeys.required,
      test(value, context) {
        const { bankNumber } = context.parent;
        // Both bankAccount and bankNumber must be filled together or both left empty
        if ((value && !bankNumber) || (!value && bankNumber)) {
          return false;
        }
        return true;
      },
    }),
  bankNumber: yup
    .string()
    .nullable()
    .test({
      name: 'isBankNumber',
      message: langKeys.invalidBankNumber,
      test: (value) => (!value ? true : /^\d{4}$/.test(value)),
    })
    .test({
      name: 'bankNumberWithBankAccount',
      message: langKeys.required,
      test(value, context) {
        const { bankAccount } = context.parent;
        // Both bankAccount and bankNumber must be filled together or both left empty
        if ((value && !bankAccount) || (!value && bankAccount)) {
          return false;
        }
        return true;
      },
    }),
  iban: yup
    .string()
    .nullable()
    .test({
      name: 'ibanWithSwift',
      message: langKeys.required,
      test(value, context) {
        const { swift } = context.parent;
        // Both iban and swift must be filled together or both left empty
        if ((value && !swift) || (!value && swift)) {
          return false;
        }
        return true;
      },
    }),
  swift: yup
    .string()
    .nullable()
    .test({
      name: 'swiftWithIban',
      message: langKeys.required,
      test(value, context) {
        const { iban } = context.parent;
        // Both iban and swift must be filled together or both left empty
        if ((value && !iban) || (!value && iban)) {
          return false;
        }
        return true;
      },
    }),
});

export const invoiceValidationSchema: yup.ObjectSchema<InvoiceFormType> = yup.object().shape(
  {
    number: yup.string().required(),
    amount: yup.number().required().min(1),
    currency: yup.string().required(),
    amountDue: yup.number().required().max(yup.ref('amount'), langKeys.amountDueGreaterThanAmount),
    issueDate: yup.string().required(),
    dueDate: yup.string().required(),
    bankAccount: yup
      .string()
      .nullable()
      .when('iban', {
        is: (val: string | null | undefined) => !!val,
        then: (s) => s.nullable().notRequired(),
        otherwise: (s) => s.required(),
      })
      .test({
        name: 'isBankAccount',
        message: langKeys.invalidBankAccount,
        test: (value) => (!value ? true : /^(\d{1,6}-)?\d{1,10}$/.test(value)),
      }),
    bankNumber: yup
      .string()
      .when('iban', {
        is: (val: string | null | undefined) => !!val,
        then: (s) => s.nullable().notRequired(),
        otherwise: (s) => s.required(),
      })
      .test({
        name: 'isBankNumber',
        message: langKeys.invalidBankNumber,
        test: (value) => (!value ? true : /^\d{4}$/.test(value)),
      }),
    iban: yup
      .string()
      .nullable()
      .when('bankAccount', {
        is: (val: string | null | undefined) => !!val,
        then: (s) => s.nullable().notRequired(),
        otherwise: (s) => s.required(),
      }),
    swift: yup.string().nullable(),
    variableSymbol: yup.string().required(),
    customer: customerValidationSchema,
    vendor: vendorValidationSchema,
    remindingTone: yup.mixed<RemindingTone>().oneOf(Object.values(RemindingTone)),
    items: yup.array().of(
      yup.object().shape({
        name: yup.string().required(),
        quantity: yup.number().required(),
        pricePerUnit: yup.number().required(),
        priceSum: yup.number().required(),
        unit: yup.string(),
      }),
    ),
  },
  [['bankAccount', 'iban']],
);

export const scenarioCreateValidationSchema: yup.ObjectSchema<ScenarioCreateInput> = yup.object({
  name: yup.string().required(),
  userId: yup.string().required(),
  modelId: yup.string().required(),
  subject: yup.array().of(yup.string().required()).required(),
  textEmail: yup.array().of(yup.string().required()).required(),
  textSms: yup.array().of(yup.string().required()).required(),
  textCall: yup.array().of(yup.string().required()).required(),
  plan: yup.mixed<ScenarioPlanName>().oneOf(Object.values(ScenarioPlanName)).required(),
});

export const invoiceManagementInvoiceValidationSchema: yup.ObjectSchema<InvoiceManagementInvoiceFormType> = yup
  .object()
  .shape(
    {
      number: yup.string().required(),
      amount: yup.number().required().min(0),
      amountWithoutVat: yup
        .number()
        .transform((value, originalValue) => {
          // When original value is null, empty string, or undefined and taxPayer is false
          // convert to undefined to avoid validation issues
          if (originalValue === null || originalValue === '' || originalValue === undefined) {
            return undefined;
          }
          return value;
        })
        .when('taxPayer', {
          is: true,
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.nullable().notRequired(),
        }),
      amountVat: yup
        .number()
        .transform((value, originalValue) => {
          // When original value is null, empty string, or undefined and taxPayer is false
          // convert to undefined to avoid validation issues
          if (originalValue === null || originalValue === '' || originalValue === undefined) {
            return undefined;
          }
          return value;
        })
        .when('taxPayer', {
          is: true,
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.nullable().notRequired(),
        }),
      currency: yup.string().required(),
      issueDate: yup.string().required(),
      dueDate: yup.string().required(),
      taxPayer: yup.boolean().required(),
      taxPoint: yup
        .string()
        .nullable()
        .when('taxPayer', {
          is: true,
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.nullable(),
        }),
      bankAccount: yup
        .string()
        .nullable()
        .when('iban', {
          is: (val: string | null | undefined) => !!val,
          then: (s) => s.nullable().notRequired(),
          otherwise: (s) => s.required(),
        })
        .test({
          name: 'isBankAccount',
          message: langKeys.invalidBankAccount,
          test: (value) => (!value ? true : /^(\d{1,6}-)?\d{1,10}$/.test(value)),
        }),
      bankNumber: yup
        .string()
        .when('iban', {
          is: (val: string | null | undefined) => !!val,
          then: (s) => s.nullable().notRequired(),
          otherwise: (s) => s.required(),
        })
        .test({
          name: 'isBankNumber',
          message: langKeys.invalidBankNumber,
          test: (value) => (!value ? true : /^\d{4}$/.test(value)),
        }),
      iban: yup
        .string()
        .nullable()
        .when('bankAccount', {
          is: (val: string | null | undefined) => !!val,
          then: (s) => s.nullable().notRequired(),
          otherwise: (s) => s.required(),
        }),
      swift: yup.string().nullable(),
      variableSymbol: yup.string().required(),
      customer: yup.object().shape({
        name: yup.string().required(),
        address: yup.string().nullable(),
        vatId: vatIdSchema,
        taxId: yup.string().nullable(),
      }),
      vendor: yup.object().shape({
        name: yup.string().required(),
        address: yup.string().nullable(),
        vatId: vatIdSchema,
        taxId: yup
          .string()
          .nullable()
          .test({
            name: 'conditionallyRequiredTaxId',
            message: langKeys.required,
            test: function (value) {
              // Get the parent form's taxPayer value
              const { from } = this;
              // Make sure 'from' exists and has at least 2 elements
              if (!from || from.length < 2) return true;

              // Navigate up to root form to get taxPayer
              const taxPayer = from[1].value.taxPayer;

              // If taxPayer is true, taxId is required
              if (taxPayer === true) {
                return value != null && value !== '';
              }

              // Not required if not a tax payer
              return true;
            },
          }),
      }),
      remindingTone: yup.mixed<RemindingTone>().oneOf(Object.values(RemindingTone)),
      items: yup.array().of(
        yup.object().shape({
          name: yup.string().required(),
          quantity: yup.number().required(),
          pricePerUnit: yup.number().required(),
          priceSum: yup.number().required(),
          vatRate: yup
            .string()
            .nullable()
            .test({
              name: 'conditionallyRequiredVatRate',
              message: langKeys.required,
              test: function (value) {
                // Get the parent form's taxPayer value
                const { from } = this;
                // Make sure 'from' exists and has at least 2 elements
                if (!from || from.length < 2) return true;

                // Navigate up to root form to get taxPayer
                const taxPayer = from[1].value.taxPayer;

                // If taxPayer is true, vatRate is required
                if (taxPayer === true) {
                  return value != null && value !== '';
                }

                // Not required if not a tax payer
                return true;
              },
            }),
          priceWithoutVat: yup
            .string()
            .nullable()
            .test({
              name: 'conditionallyRequiredPriceWithoutVat',
              message: langKeys.required,
              test: function (value) {
                // Get the parent form's taxPayer value
                const { from } = this;
                // Make sure 'from' exists and has at least 2 elements
                if (!from || from.length < 2) return true;

                // Navigate up to root form to get taxPayer
                const taxPayer = from[1].value.taxPayer;

                // If taxPayer is true, priceWithoutVat is required
                if (taxPayer === true) {
                  return value != null && value !== '';
                }

                // Not required if not a tax payer
                return true;
              },
            }),
          unit: yup.string(),
        }),
      ),
    },
    [['bankAccount', 'iban']],
  ) as yup.ObjectSchema<InvoiceManagementInvoiceFormType>;
